import {Exclude, Expose, instanceToPlain} from 'class-transformer';
import {IsArray, IsBoolean, IsNumber, IsString} from 'class-validator';
import {MinimalUserModel} from './user.entity';

export class UserGroupMetadata {
	@Expose() @IsString() icon: string = 'fas fa-users';
	@Expose() @IsString() color: string = '#213769';
	@Expose() @IsString() bgColor: string = '#21376959';

	constructor(init?: Partial<UserGroupMetadata>) {
		if (!init) return;
		this.icon = init?.icon ?? 'fas fa-users';
		this.color = init?.color ?? '#213769';
		this.bgColor = init?.bgColor ?? '#21376959';
	}

	toJSON() {
		return instanceToPlain(this);
	}
}

export class UserGroup {
	@Expose() @IsString() id: string;
	@Expose() @IsBoolean() isEnabled: boolean;
	@Expose() @IsBoolean() locked: boolean;
	@Expose() @IsString() name: string;
	@Expose() @IsString() description: string;
	@Expose() metadata: UserGroupMetadata;

	@Expose() @IsArray() @IsString({each: true}) users: string[];
	@Exclude() @IsArray() userModels: MinimalUserModel[];

	@Exclude() @IsNumber() usersCount: number = 0;

	constructor(init?: Partial<UserGroup>) {
		this.id = init?.id ?? '';
		this.isEnabled = init?.isEnabled ?? true;
		this.name = init?.name ?? '';
		this.description = init?.description ?? '';
		this.locked = init?.locked ?? false;
		this.setGroupUsers(init?.users ?? []);
		this.metadata = new UserGroupMetadata(init?.metadata ?? {});
	}

	public setGroupUsers(users: (MinimalUserModel | string)[], override = false) {
		if (override) this.users = null;
		if (override) this.userModels = null;
		users.forEach(user => {
			if (typeof user === 'string') {
				if (!this.users) this.users = [];
				this.users.push(user);
			} else if (MinimalUserModel.isMinimalUserModel(user)) {
				if (!this.userModels) this.userModels = [];
				this.userModels.push(new MinimalUserModel(user));
			}
		});
		// remove duplicates
		if (this.users) {
			this.users = [...new Set(this.users)];
			this.usersCount = this.users.length;
		}
		if (this.userModels) {
			// remove duplicates by id
			this.userModels = this.userModels.filter((user, index, self) => index === self.findIndex(t => t.id === user.id));
			this.usersCount = this.userModels.length;
		}
		if (this.users && this.userModels) {
			this.userModels = this.userModels.filter(user => this.users.includes(user.id));
		}
	}

	public merge(group: UserGroup): UserGroup {
		this.setGroupUsers(group.users, true);
		this.metadata = new UserGroupMetadata(group.metadata);
		return this;
	}

	public clone(): UserGroup {
		return new UserGroup(this);
	}

	toJSON() {
		if (!this.users) {
			this.users = this.userModels?.map(user => user.id) ?? [];
		}
		return instanceToPlain(this);
	}
}

// Type alias to maintain compatibility with existing code
export type IUserGroup_ = UserGroup;
